// Hook personnalisé pour utiliser l'API des explications (ancien système)
// ⚠️ Ce hook est conservé pour compatibilité mais utilise maintenant directement l'API réelle
// Utilisez plutôt useRequestsApi pour les nouveaux développements

import { useCallback } from "react";

// Import des services réels
import * as realApi from "../server/Explanations CRUD/Requests";

// Types
import {
  CreateExplanationRequestInput,
  ProposeTimeSlotsInput,
  SelectTimeSlotInput,
  AssignTeacherInput,
  RejectRequestInput,
  UpdateRequestStatusInput,
  GetRequestsFiltersInput,
} from "../server/Explanations CRUD/InputsTypes";

// Hook principal
export const useExplanationApi = () => {
  // Utiliser uniquement l'API réelle
  const api = realApi;

  // ===== ÉLÈVES =====

  const createExplanationRequest = useCallback(
    (input: CreateExplanationRequestInput) => {
      return api.createExplanationRequest(input);
    },
    [api],
  );

  const getMyExplanationRequests = useCallback(() => {
    return api.getMyExplanationRequests();
  }, [api]);

  const selectTimeSlot = useCallback(
    (input: SelectTimeSlotInput) => {
      return api.selectTimeSlot(input);
    },
    [api],
  );

  const selectTimeSlotById = useCallback(
    (requestId: string, slotId: string) => {
      return api.selectTimeSlot({ requestId, slotId });
    },
    [api],
  );

  // ===== PROFESSEURS =====

  const getAssignedExplanationRequests = useCallback(() => {
    return api.getAssignedExplanationRequests();
  }, [api]);

  const proposeTimeSlots = useCallback(
    (input: ProposeTimeSlotsInput) => {
      return api.proposeTimeSlots(input);
    },
    [api],
  );

  const getTeacherAvailability = useCallback(() => {
    return api.getTeacherAvailability();
  }, [api]);

  const updateTeacherAvailability = useCallback(
    (slots: { startTime: Date; endTime: Date }[]) => {
      return api.updateTeacherAvailability(slots);
    },
    [api],
  );

  // ===== ADMINISTRATEURS =====

  const getPendingExplanationRequests = useCallback(
    (filters?: GetRequestsFiltersInput) => {
      return api.getPendingExplanationRequests(filters);
    },
    [api],
  );

  const getExplanationStats = useCallback(() => {
    return api.getExplanationStats();
  }, [api]);

  const getAvailableTeachers = useCallback(() => {
    return api.getAvailableTeachers();
  }, [api]);

  const assignTeacher = useCallback(
    (input: AssignTeacherInput) => {
      return api.assignTeacher(input);
    },
    [api],
  );

  const assignTeacherById = useCallback(
    (requestId: string, teacherId: string, adminNotes?: string) => {
      return api.assignTeacher({ requestId, teacherId, adminNotes });
    },
    [api],
  );

  const rejectRequest = useCallback(
    (input: RejectRequestInput) => {
      return api.rejectRequest(input);
    },
    [api],
  );

  const rejectRequestById = useCallback(
    (requestId: string, reason: string, adminNotes?: string) => {
      return api.rejectRequest({ requestId, reason, adminNotes });
    },
    [api],
  );

  // ===== DONNÉES DE RÉFÉRENCE =====

  const getSubjects = useCallback(() => {
    return api.getSubjects();
  }, [api]);

  const getStudentClasses = useCallback(() => {
    return api.getStudentClasses();
  }, [api]);

  // ===== NOTIFICATIONS =====

  const getExplanationNotifications = useCallback(() => {
    return api.getExplanationNotifications();
  }, [api]);

  const markNotificationAsRead = useCallback(
    (notificationId: string) => {
      return api.markNotificationAsRead(notificationId);
    },
    [api],
  );

  // ===== UTILITAIRES =====

  const getExplanationRequestDetails = useCallback(
    (requestId: string) => {
      return api.getExplanationRequestDetails(requestId);
    },
    [api],
  );

  const updateRequestStatus = useCallback(
    (input: UpdateRequestStatusInput) => {
      return api.updateRequestStatus(input);
    },
    [api],
  );

  // Retourner toutes les fonctions
  return {
    // Configuration (toujours false maintenant)
    isUsingMockData: false,

    // Élèves
    createExplanationRequest,
    getMyExplanationRequests,
    selectTimeSlot,
    selectTimeSlotById,

    // Professeurs
    getAssignedExplanationRequests,
    proposeTimeSlots,
    getTeacherAvailability,
    updateTeacherAvailability,

    // Administrateurs
    getPendingExplanationRequests,
    getExplanationStats,
    getAvailableTeachers,
    assignTeacher,
    assignTeacherById,
    rejectRequest,
    rejectRequestById,

    // Données de référence
    getSubjects,
    getStudentClasses,

    // Notifications
    getExplanationNotifications,
    markNotificationAsRead,

    // Utilitaires
    getExplanationRequestDetails,
    updateRequestStatus,
  };
};

// Hook pour les données de référence (matières, classes)
export const useReferenceData = () => {
  const { getSubjects, getStudentClasses } = useExplanationApi();

  return {
    getSubjects,
    getStudentClasses,
  };
};

// Hook pour les statistiques
export const useExplanationStats = () => {
  const { getExplanationStats } = useExplanationApi();

  return {
    getExplanationStats,
  };
};

// Hook pour les notifications
export const useExplanationNotifications = () => {
  const { getExplanationNotifications, markNotificationAsRead } =
    useExplanationApi();

  return {
    getExplanationNotifications,
    markNotificationAsRead,
  };
};

// Hook pour les professeurs disponibles
export const useAvailableTeachers = () => {
  const { getAvailableTeachers } = useExplanationApi();

  return {
    getAvailableTeachers,
  };
};

// Hook pour les disponibilités du professeur
export const useTeacherAvailability = () => {
  const { getTeacherAvailability, updateTeacherAvailability } =
    useExplanationApi();

  return {
    getTeacherAvailability,
    updateTeacherAvailability,
  };
};

// Export par défaut
export default useExplanationApi;
